import { useState, useEffect } from "react";
import { CheckOnboardingIssue, CheckCompany, CheckSignatory, CheckForm, isCheckError } from "winslow-core";
import { ModalComponentWrapper } from "../../shared/ModalComponentWrapper";
import { Alert, AlertDescription } from "../../../ds2/components";
import { apiCheckRenderForm, apiCheckGetCompanyForm } from "../../../services/PayrollService";
import { checkGenerateFormDefaults } from "./checkFormDefaults";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";

type ModalCheckFormViewerProps = {
    isOpen: boolean;
    filingAuthorization: CheckOnboardingIssue | null;
    checkCompany: CheckCompany | null;
    checkSignatory: CheckSignatory | null;
    ein: string | null;
    onClose: () => void;
};

export const ModalCheckFormViewer = ({
    isOpen,
    filingAuthorization,
    checkCompany,
    checkSignatory,
    ein,
    onClose,
}: ModalCheckFormViewerProps) => {
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [loadingRenderedForm, setLoadingRenderedForm] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedForm, setSelectedForm] = useState<CheckForm | null>(null);

    // Load rendered form when modal opens
    useEffect(() => {
        if (isOpen && filingAuthorization && checkCompany && checkSignatory && ein) {
            loadRenderedForm();
        } else {
            // Clean up PDF URL when modal closes
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
            }
            setPdfBlob(null);
            setError(null);
            setSelectedForm(null);
        }
    }, [isOpen, filingAuthorization, checkCompany, checkSignatory, ein]);

    // Clean up PDF URL on unmount
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    const loadRenderedForm = async () => {
        if (!filingAuthorization || !checkCompany || !checkSignatory || !ein) {
            return;
        }

        setLoadingRenderedForm(true);
        setError(null);

        try {
            // First, get the form details to generate parameters
            const formResponse = await apiCheckGetCompanyForm(checkCompany.id, filingAuthorization.id);
            const form = formResponse?.data;

            if (!formResponse || isCheckError(formResponse) || !form) {
                toast("Error loading form details", toastFail);
                setError("Failed to load form details");
                return;
            }

            setSelectedForm(form);

            // Generate parameters from form defaults
            const parameters = checkGenerateFormDefaults(form, checkCompany, checkSignatory, ein);

            // Render the form with parameters
            const blob = await apiCheckRenderForm(filingAuthorization.id, parameters);

            if (!blob) {
                toast("Error loading form", toastFail);
                setError("Failed to load form");
                return;
            }

            setPdfBlob(blob);
            // Create object URL for the PDF blob
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (error) {
            console.error("[ModalCheckFormViewer] Error loading rendered form:", error);
            toast("Error loading form", toastFail);
            setError("Failed to load form");
        } finally {
            setLoadingRenderedForm(false);
        }
    };

    console.log("[ModalCheckFormViewer] filingAuthorization: ", filingAuthorization);
    return (
        <ModalComponentWrapper
            isOpen={isOpen}
            onClose={onClose}
            style={{ width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
        >
            <div style={{ padding: "24px" }}>
                {error && (
                    <Alert variant="warning" style={{ marginBottom: "16px" }}>
                        <AlertDescription>
                            {error}
                        </AlertDescription>
                    </Alert>
                )}

                {loadingRenderedForm ? (
                    <div style={{ padding: "40px", textAlign: "center" }}>
                        Loading form...
                    </div>
                ) : pdfUrl ? (
                    <div>
                        {filingAuthorization && (
                            <h2 style={{ marginBottom: "16px", fontSize: "20px", fontWeight: "600" }}>
                                {filingAuthorization.name}
                            </h2>
                        )}
                        <iframe
                            src={pdfUrl}
                            style={{
                                width: "100%",
                                height: "70vh",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                            }}
                            title={filingAuthorization?.name || "Form PDF"}
                        />
                    </div>
                ) : (
                    <Alert variant="warning">
                        <AlertDescription>
                            Form not found.
                        </AlertDescription>
                    </Alert>
                )}
            </div>
        </ModalComponentWrapper>
    );
};

